/**
 * AdminReviewTabs — grouped top-level tab navigation for the Admin console
 * (founder live-testing feedback 2026-07-10: "left menu is getting long,
 * needs a tab rework").
 *
 * Replaces the X.3.7 left rail with TWO horizontal rows:
 *   1. a top-level tab per SECTION (Directory / People / Activity /
 *      AI tools / Billing) — the ClientRecordTabs underline register;
 *   2. a secondary pill row of the active section's VIEWS (only when the
 *      section has more than one visible view) — the filter-chip register.
 *
 * The flat view keys are preserved as URL ?tab= values so existing
 * deep-links + the legacy /lookups,/suggestions,/bulk-add redirects keep
 * working; the old 'team' and 'staff' keys normalize to 'people' and the
 * retired 'billing-rules' key lands on 'rate-bands' (handled in
 * AdminReview). Clicking a section opens its first visible view.
 *
 * The Directory section (and its Review queue view) carries a count badge
 * when count > 0 (mirrors the AppShell badge semantic, "needs your
 * attention right now").
 */

import { type ReactNode } from 'react';
import { TAB_RAIL_CLASSES } from '@/components/primitives/tabRail';
import { SelectField } from '@/components/primitives/SelectField';

export type AdminReviewTab =
  | 'pending'
  | 'bulk'
  | 'merges'
  | 'lookups'
  | 'people'
  | 'activity'
  // ADR-052 — everything that failed, with the reference to quote
  // (view_errors: the owner and admin support).
  | 'errors'
  | 'ai-usage'
  | 'ai-limits'
  | 'ai-prompts'
  | 'email-editor'
  // Rates administration — the tenant's band vocabulary editor
  // (tenants.config features.rate_bands; manage_team, like Limits & access).
  | 'rate-band-editor'
  // 0134 — read-only "which rate bands each role may pick" matrix (manage_team).
  | 'rate-bands'
  // B1-D3c — per-client per-band rate grid (manage_client_rates — the
  // PUT's gate; owner + the bookkeeping seats, NOT manage_team).
  | 'client-rates'
  // The QuickBooks settings: the connection, the customer-link ceremony,
  // and the rate-band item map. A billing configuration noun, so it sits
  // with the other three (manage_billing_integration,
  // connect_billing_integration OR manage_qbo_customer_links).
  | 'quickbooks'
  // RBAC Permissions screen (ADR-0046, migration 0157) — the owner matrix
  // over per-tenant role-capability overrides (manage_permissions gate).
  | 'permissions'
  // Notifications part two — the tenant-wide defaults grid
  // (tenants.config notifications.defaults; manage_team, the same gate
  // as the Rate bands editor above — reuses hideAiToolsTabs).
  | 'notification-defaults'
  // migration 0185 — the counties a unit covers (manage_team, the same
  // gate as the two views above — reuses hideAiToolsTabs).
  | 'unit-coverage'
  // The map's operator-configurable colors: each unit's dot plus the
  // tenant's client/prospect/placement-pin colors (manage_team, the same
  // gate as the two views above — reuses hideAiToolsTabs; GET
  // /map/colors + PUT /admin/map/colors + PUT /admin/units/{id}/color,
  // api/src/functions/mapColors.ts).
  | 'map-colours'
  // The two colours SPICE sessions wear (tenants.config.spice.kind_colors;
  // manage_team, the same gate as Map colours: reuses hideAiToolsTabs; PUT
  // /admin/spice/kind-colors, api/src/functions/spiceKindColors.ts).
  | 'spice-colours';

export const VALID_TABS: ReadonlyArray<AdminReviewTab> = [
  'pending',
  'bulk',
  'merges',
  'lookups',
  'people',
  'activity',
  'errors',
  'ai-usage',
  'ai-limits',
  'ai-prompts',
  'email-editor',
  'rate-band-editor',
  'rate-bands',
  'client-rates',
  'quickbooks',
  'permissions',
  'notification-defaults',
  'unit-coverage',
  'map-colours',
  'spice-colours',
];

// Plain-language view labels (X.3.7 Part 3 copy pass). Engineer-facing terms
// ("Lookups", "Merges", "Bulk add") are gone.
export const VIEW_LABELS: Record<AdminReviewTab, string> = {
  pending: 'Review queue',
  bulk: 'Add organizations',
  merges: 'Duplicates',
  lookups: 'Categories & tags',
  people: 'Access & Information',
  activity: 'Change log',
  errors: 'Errors',
  'ai-usage': 'Usage',
  'ai-limits': 'Limits & access',
  'ai-prompts': 'Prompts',
  'email-editor': 'Email editor',
  'rate-band-editor': 'Rate bands',
  'rate-bands': 'Rate bands by role',
  'client-rates': 'Client rates',
  quickbooks: 'QuickBooks',
  permissions: 'Permissions',
  'notification-defaults': 'Defaults',
  'unit-coverage': 'Counties',
  'map-colours': 'Map colours',
  'spice-colours': 'SPICE colours',
};

// All three AI-tools views are now live: Usage (X.3.7b #234), Limits & access
// (C1b #237), Prompts (C3). No "Soon" stubs remain; kept as an empty tuple so
// the badge/select-suffix logic stays in place for any future stub.
export const STUB_TABS: ReadonlyArray<AdminReviewTab> = [];

// The whole "AI tools" section's views — gated together by manage_team (the
// same capability the Usage endpoint enforces) via hideAiToolsTabs.
const AI_TOOLS_TABS: ReadonlyArray<AdminReviewTab> = ['ai-usage', 'ai-limits', 'ai-prompts', 'email-editor'];

// Slice 3c — the steward-only Directory WRITE views (their data endpoints
// gate on manage_lookups/direct_write, not manage_staff_profiles). Hidden
// together via hideStewardTabs so a profiles-only role (admin_support/
// exec_support) that is newly admitted to the page sees ONLY the People
// section. ('merges' has its own merge_providers gate via hideMergesTab.)
// 2026-07-14: 'pending' + 'activity' split OFF this list — their endpoints
// gate on read_all_suggestions (the queue list scope + the admin-review
// timeline), so they key on hideQueueTabs instead; the read-only
// board_observer holds read_all_suggestions without manage_lookups.
const STEWARD_ONLY_TABS: ReadonlyArray<AdminReviewTab> = ['bulk', 'lookups'];

// The read_all_suggestions-gated views (founder ruling 2026-07-14): the
// Review queue (list endpoints scope on read_all_suggestions) + the Change
// log (the /admin-review/timeline endpoint's gate). Hidden together via
// hideQueueTabs. Approve/apply affordances INSIDE the queue gate separately
// on 'approve' (see SuggestionReviewPane / ResourceProposalReviewPane).
const QUEUE_READ_TABS: ReadonlyArray<AdminReviewTab> = ['pending', 'activity'];

interface AdminSection {
  label: string;
  items: ReadonlyArray<AdminReviewTab>;
}

const SECTIONS: ReadonlyArray<AdminSection> = [
  { label: 'Directory', items: ['pending', 'bulk', 'merges', 'lookups'] },
  { label: 'People', items: ['people'] },
  { label: 'Activity', items: ['activity', 'errors'] },
  { label: 'AI tools', items: ['ai-usage', 'ai-limits', 'ai-prompts', 'email-editor'] },
  // 'billing-rules' retired 2026-07-10 (founder feedback): its substance
  // lives as the Pre-billing states disclosure + docs/sops/billing-readiness-sop.md.
  { label: 'Billing', items: ['rate-band-editor', 'rate-bands', 'client-rates', 'quickbooks'] },
  // RBAC Permissions screen (ADR-0046, migration 0157) — owner-only.
  { label: 'Permissions', items: ['permissions'] },
  // Notifications part two — the tenant-wide defaults grid.
  { label: 'Notifications', items: ['notification-defaults'] },
  // migration 0185 — the counties a unit covers, and the same section's
  // map colors.
  { label: 'Units', items: ['unit-coverage', 'map-colours', 'spice-colours'] },
];

/** Stable DOM id fragment for a section ("AI tools" → "ai-tools"). */
function sectionSlug(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '-');
}

/** Per-view visibility flags shared by the tab rows and the mobile select. */
interface TabVisibility {
  /**
   * When true, the 'people' view is hidden. Visible to the owner AND the
   * manage_staff_profiles seats (admin_support / exec_support /
   * supervising_cm), who get the bio/photo editor there, not the owner's
   * Login & access controls. Server still gates data + mutations via
   * manage_team (owner surfaces) and manage_staff_profiles (profile edits).
   */
  hidePeopleTab?: boolean;
  /**
   * When true, the 'merges' (Duplicates) view is hidden. Server gates via the
   * merge_providers capability; this mirrors it (CMs do not see Duplicates).
   */
  hideMergesTab?: boolean;
  /**
   * When true, the whole 'AI tools' section is hidden. Server gates the views
   * via manage_team; this mirrors it so a steward-but-not-manage_team user
   * (e.g. data_steward) doesn't see tabs whose endpoints 403.
   */
  hideAiToolsTabs?: boolean;
  /**
   * Slice 3c — when true, the steward-only Directory write views (bulk /
   * lookups) are hidden. Set for a profiles-only role
   * (manage_staff_profiles without manage_lookups) so the page shows ONLY the
   * People section.
   */
  hideStewardTabs?: boolean;
  /**
   * 2026-07-14 — when true, the read_all_suggestions-gated views (pending /
   * activity) are hidden. Their endpoints gate on read_all_suggestions, a
   * READ capability the board_observer holds; keying them on the steward
   * flag (manage_lookups) kept the read-only board seat out of surfaces its
   * capability admits.
   */
  hideQueueTabs?: boolean;
  /** ADR-052 — the Errors view mirrors its endpoint's gate (view_errors). */
  hideErrorsTab?: boolean;
  /**
   * B1-D3c — when true, the 'client-rates' view is hidden. Server gates the
   * grid + the PUT via manage_client_rates (owner + bookkeeping seats); this
   * mirrors it. Its OWN flag (not hideAiToolsTabs): exec_support holds
   * manage_client_rates without manage_team, so the bookkeeper sees Billing >
   * Client rates and nothing else in the section.
   */
  hideClientRatesTab?: boolean;
  /**
   * ADR-0046 — when true, the 'permissions' view is hidden. Server gates
   * the matrix + writes via manage_permissions, itself hard-locked to the
   * owner row, so this flag is effectively owner-only; still keyed on the
   * capability (not a role check) for consistency with the other tabs.
   */
  hidePermissionsTab?: boolean;
  /**
   * When true, the 'quickbooks' view is hidden. The view holds surfaces
   * with THREE server gates — manage_billing_integration (disconnect),
   * connect_billing_integration (connect, reconnect) and
   * manage_qbo_customer_links (the link ceremony, the item map) — so
   * the flag mirrors their UNION. Keyed on one alone
   * and the seat that runs month end loses the item map its own failed
   * pushes send it to.
   */
  hideQuickBooksTab?: boolean;
}

/** Shared rule for whether a view is filtered out of the tabs + the select. */
function makeIsHidden(v: TabVisibility): (t: AdminReviewTab) => boolean {
  return (t: AdminReviewTab): boolean =>
    (t === 'people' && !!v.hidePeopleTab) ||
    (t === 'merges' && !!v.hideMergesTab) ||
    (t === 'errors' && !!v.hideErrorsTab) ||
    (AI_TOOLS_TABS.includes(t) && !!v.hideAiToolsTabs) ||
    // The Rate bands editor + the 0134 Rate bands by role matrix —
    // manage_team, the same gate as AI tools (their endpoints 403 without it).
    (t === 'rate-band-editor' && !!v.hideAiToolsTabs) ||
    (t === 'rate-bands' && !!v.hideAiToolsTabs) ||
    // Notifications tenant defaults — manage_team, the same gate.
    (t === 'notification-defaults' && !!v.hideAiToolsTabs) ||
    // migration 0185 — unit coverage counties, manage_team, the same gate.
    (t === 'unit-coverage' && !!v.hideAiToolsTabs) ||
    // The map's operator-configurable colors, manage_team, the same gate.
    (t === 'map-colours' && !!v.hideAiToolsTabs) ||
    // The SPICE session colours, manage_team, the same gate.
    (t === 'spice-colours' && !!v.hideAiToolsTabs) ||
    // B1-D3c Client rates — manage_client_rates (its endpoint's gate).
    (t === 'client-rates' && !!v.hideClientRatesTab) ||
    // QuickBooks settings — manage_billing_integration,
    // connect_billing_integration OR manage_qbo_customer_links (the
    // union of its contents' gates).
    (t === 'quickbooks' && !!v.hideQuickBooksTab) ||
    // ADR-0046 Permissions — manage_permissions (its endpoint's gate).
    (t === 'permissions' && !!v.hidePermissionsTab) ||
    (STEWARD_ONLY_TABS.includes(t) && !!v.hideStewardTabs) ||
    (QUEUE_READ_TABS.includes(t) && !!v.hideQueueTabs);
}

/**
 * Whether a tab is hidden for the given visibility — exported so the page
 * (AdminReview) coerces its active tab with the SAME logic as the tabs (single
 * source of truth; a profiles-only role URL-hacking ?tab=pending falls back to
 * a visible tab rather than a 403 shell).
 */
export function isAdminTabHidden(tab: AdminReviewTab, visibility: TabVisibility): boolean {
  return makeIsHidden(visibility)(tab);
}

/** The first VALID_TABS entry visible for the given visibility — the default
 *  landing tab (e.g. 'people' for a profiles-only role that can't see 'pending'). */
export function firstVisibleAdminTab(visibility: TabVisibility): AdminReviewTab {
  const isHidden = makeIsHidden(visibility);
  return VALID_TABS.find((t) => !isHidden(t)) ?? 'people';
}

export interface AdminReviewTabsProps extends TabVisibility {
  activeTab: AdminReviewTab;
  pendingCount: number | null;
  onChange: (next: AdminReviewTab) => void;
}

/** The count badge shared by the Directory section tab and the Review-queue pill. */
function CountBadge({ count }: { count: number }): ReactNode {
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-pill bg-sage-800 text-cream text-xs font-semibold"
    >
      {count}
    </span>
  );
}

export function AdminReviewTabs({
  activeTab,
  pendingCount,
  hidePeopleTab,
  hideMergesTab,
  hideAiToolsTabs,
  hideStewardTabs,
  hideQueueTabs,
  hideClientRatesTab,
  hidePermissionsTab,
  hideQuickBooksTab,
  hideErrorsTab,
  onChange,
}: AdminReviewTabsProps): ReactNode {
  const isHidden = makeIsHidden({
    hidePeopleTab,
    hideMergesTab,
    hideAiToolsTabs,
    hideStewardTabs,
    hideQueueTabs,
    hideClientRatesTab,
    hidePermissionsTab,
    hideQuickBooksTab,
    hideErrorsTab,
  });

  const visibleSections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((t) => !isHidden(t)),
  })).filter((section) => section.items.length > 0);

  const activeSection = visibleSections.find((s) => s.items.includes(activeTab));

  return (
    <>
      <nav aria-label="Admin sections" className="hidden md:block w-full">
        {/* Top level: one tab per section (the client record's tab-row register). */}
        <div className="overflow-x-auto">
          <div
            role="tablist"
            aria-label="Admin sections"
            className={`flex gap-1 min-w-max ${TAB_RAIL_CLASSES.tablist}`}
          >
            {visibleSections.map((section) => {
              const isActive = section === activeSection;
              const showBadge =
                section.items.includes('pending') && pendingCount !== null && pendingCount > 0;
              return (
                <button
                  key={section.label}
                  id={`admin-section-${sectionSlug(section.label)}`}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="admin-tabpanel"
                  aria-label={
                    showBadge ? `${section.label}, ${pendingCount} pending` : section.label
                  }
                  onClick={() => {
                    // Entering a section lands on its first visible view;
                    // re-clicking the active section keeps the current view.
                    if (!isActive) onChange(section.items[0]);
                  }}
                  className={
                    `${TAB_RAIL_CLASSES.tab} ` +
                    (isActive
                      ? `border-forest-700 ${TAB_RAIL_CLASSES.active}`
                      : TAB_RAIL_CLASSES.inactive)
                  }
                >
                  {section.label}
                  {showBadge && <CountBadge count={pendingCount} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Second level: the active section's views as pills (filter-chip
            register), only when there is more than one to choose from. */}
        {activeSection && activeSection.items.length > 1 && (
          <div
            role="tablist"
            aria-label={`${activeSection.label} views`}
            className="mt-3 flex flex-wrap items-center gap-2"
          >
            {activeSection.items.map((tab) => {
              const isActive = tab === activeTab;
              const isStub = STUB_TABS.includes(tab);
              const showBadge = tab === 'pending' && pendingCount !== null && pendingCount > 0;
              const label = VIEW_LABELS[tab];
              return (
                <button
                  key={tab}
                  id={`admin-tab-${tab}`}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="admin-tabpanel"
                  aria-label={showBadge ? `${label}, ${pendingCount} pending` : label}
                  onClick={() => onChange(tab)}
                  className={
                    'min-h-9 flex items-center gap-2 px-3 py-1 text-sm font-medium rounded-pill transition-colors duration-[var(--dur-base)] ease-quiet focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                    (isActive
                      ? 'bg-sage-100 text-sage-700 border border-sage-300'
                      : 'bg-paper text-ink-soft border border-sage-800/[8%] hover:border-sage-400/30')
                  }
                >
                  {label}
                  {isStub && (
                    <span
                      aria-hidden="true"
                      className="text-[10px] font-semibold uppercase tracking-wide text-ink-soft/80 bg-gold-soft rounded-pill px-1.5 py-0.5"
                    >
                      Soon
                    </span>
                  )}
                  {showBadge && <CountBadge count={pendingCount} />}
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* The same views as a native select below md, where the tab rows
          above are hidden. */}
      <AdminSectionSelect
        activeTab={activeTab}
        hidePeopleTab={hidePeopleTab}
        hideMergesTab={hideMergesTab}
        hideAiToolsTabs={hideAiToolsTabs}
        hideStewardTabs={hideStewardTabs}
        hideQueueTabs={hideQueueTabs}
        hideClientRatesTab={hideClientRatesTab}
        hidePermissionsTab={hidePermissionsTab}
        hideQuickBooksTab={hideQuickBooksTab}
        hideErrorsTab={hideErrorsTab}
        onChange={onChange}
        className="w-full md:hidden"
      />
    </>
  );
}

export interface AdminSectionSelectProps extends TabVisibility {
  activeTab: AdminReviewTab;
  onChange: (next: AdminReviewTab) => void;
  /** Caller supplies the responsive visibility (e.g. "md:hidden"). */
  className?: string;
}

/**
 * Mobile view-switcher: a native <select> built from the same SECTIONS as the
 * tab rows, with an <optgroup> per section. The desktop tab rows are hidden
 * below `md` and this compact control replaces them. Native select =
 * mobile-native picker, accessible by default (keyboard + screen reader), and
 * far less code than a drawer.
 *
 * STUB_TABS views ("Soon") stay selectable but are suffixed " (soon)" so the
 * active tab always maps to a real option and mobile keeps parity with the
 * desktop rows (where the stub buttons are clickable and render the
 * placeholder). The count badge is intentionally omitted (label-only is fine
 * on mobile).
 */
export function AdminSectionSelect({
  activeTab,
  hidePeopleTab,
  hideMergesTab,
  hideAiToolsTabs,
  hideStewardTabs,
  hideQueueTabs,
  hideClientRatesTab,
  hidePermissionsTab,
  hideQuickBooksTab,
  hideErrorsTab,
  onChange,
  className,
}: AdminSectionSelectProps): ReactNode {
  const isHidden = makeIsHidden({
    hidePeopleTab,
    hideMergesTab,
    hideAiToolsTabs,
    hideStewardTabs,
    hideQueueTabs,
    hideClientRatesTab,
    hidePermissionsTab,
    hideQuickBooksTab,
    hideErrorsTab,
  });

  return (
    <div className={className}>
      <label htmlFor="admin-section-select" className="sr-only">
        Admin section
      </label>
      <SelectField size="default"
        id="admin-section-select"
        aria-label="Admin section"
        value={activeTab}
        onChange={(e) => onChange(e.target.value as AdminReviewTab)}
        className="w-full"
      >
        {SECTIONS.map((section) => {
          const items = section.items.filter((t) => !isHidden(t));
          if (items.length === 0) return null;
          return (
            <optgroup key={section.label} label={section.label}>
              {items.map((tab) => (
                <option key={tab} value={tab}>
                  {VIEW_LABELS[tab]}
                  {STUB_TABS.includes(tab) ? ' (soon)' : ''}
                </option>
              ))}
            </optgroup>
          );
        })}
      </SelectField>
    </div>
  );
}
